import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { array, bool, object, shape, string } from 'prop-types';
import { PrismicRichText } from '@prismicio/react';
import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
} from '../../components';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PrismicSectionHero from '../../components/PrismicSectionHero/PrismicSectionHero';
import BlogListing from '../../components/BlogListing/BlogListing';
import { formatBlogPrismicData } from '../../util/data';
import { propTypes } from '../../util/types';

import css from './SingleBlogPage.module.css';

const formatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

const imageSize = {
  width: 1200,
  height: 600,
};

const SingleBlogPageComponent = props => {
  const {
    params: rawParams,
    intl,
    blog,
    scrollingDisabled,
    relatedBlogs = [],
    fetchBlogError,
    fetchBlogInProgress,
  } = props;

  if (fetchBlogInProgress) {
    const loadingTitle = intl.formatMessage({
      id: 'SingleBlogPage.loadingListingTitle',
    });

    return (
      <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer hideSearchbar />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <p className={css.loadingText}>
              <FormattedMessage id="SingleBlogPage.loadingMessage" />
            </p>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const { slug } = rawParams;
  const {
    postDate,
    writer,
    title,
    image,
    content,
    metaTitle,
    metaDescription,
    alt,
  } = formatBlogPrismicData(blog);
  const schemaTitle =
    metaTitle || title || intl.formatMessage({ id: 'SingleBlogPage.schemaTitle' });
  const hasRelatedPosts = relatedBlogs.length > 0;

  const facebookImages = [
    {
      name: 'facebook',
      url: `${image}&w=1200&h=600`,
      ...imageSize,
    },
  ];

  const twitterImages = [
    {
      name: 'twitter',
      url: `${image}&w=1200&h=600`,
      ...imageSize,
    },
  ];

  return (
    <Page
      title={schemaTitle}
      description={metaDescription}
      scrollingDisabled={scrollingDisabled}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      contentType="website"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: metaDescription,
        name: schemaTitle,
        image: image,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PrismicSectionHero
            backgroundImage={image}
            intl={intl}
            linkParams={{ slug }}
            className={css.blogSectionHero}
            alt={alt}
            disableLink
          />
          <div className={css.wrapper}>
            {fetchBlogError && (
              <p className={css.error}>
                <FormattedMessage id="SingleBlogPage.errorBlogs" />
              </p>
            )}
            <h1 className={css.title}>{title}</h1>
            <p className={css.metaInfo}>
              {postDate && postDate.toLocaleDateString('en-US', formatDateOptions)}
            </p>
            <p className={css.postedBy}>
              {intl.formatMessage({ id: 'SingleBlogPage.postedBy' }, { writer })}
            </p>
            <div className={css.contentWrapper}>
              <PrismicRichText field={content} />
            </div>
            {hasRelatedPosts && (
              <>
                <h2 className={css.heading}>
                  {intl.formatMessage({ id: 'SingleBlogPage.relatedPosts' })}
                </h2>
                <div className={css.blogs}>
                  {relatedBlogs.map(blog => (
                    <BlogListing key={blog.id} blog={blog} className={css.blog} />
                  ))}
                </div>
              </>
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

SingleBlogPageComponent.defaultProps = {
  params: null,
  blog: null,
  relatedBlogs: [],
  fetchBlogError: null,
  fetchBlogInProgress: false,
};

SingleBlogPageComponent.propTypes = {
  params: shape({
    slug: string,
  }),
  blog: object,
  scrollingDisabled: bool.isRequired,
  relatedBlogs: array,
  fetchBlogError: propTypes.error,
  fetchBlogInProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { fetchBlogInProgress, fetchBlogError, blog, relatedBlogs } = state.SingleBlogPage;

  return {
    fetchBlogInProgress,
    fetchBlogError,
    blog,
    scrollingDisabled: isScrollingDisabled(state),
    relatedBlogs,
  };
};

const SingleBlogPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;
